import React, { useState } from 'react';

const CaseCredentials = ({ credentials, onAdd }) => {
    const [form, setForm] = useState({
        label: '',
        username: '',
        password: '',
        url: '',
        notes: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAdd = () => {
        if (!form.label && !form.username && !form.url) return;
        onAdd(form);
        setForm({ label: '', username: '', password: '', url: '', notes: '' });
    };

    return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden">
            <div className="bg-gray-900/60 px-4 py-3 border-b border-gray-700 flex items-center justify-between">
                <h3 className="text-sm font-bold text-white tracking-wider flex items-center gap-2">
                    <span>🔐</span>
                    <span>Online Accounts & Credentials</span>
                </h3>
            </div>
            <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <input
                        name="label"
                        value={form.label}
                        onChange={handleChange}
                        className="md:col-span-1 w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-xs text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                        placeholder="Portal / Account"
                    />
                    <input
                        name="username"
                        value={form.username}
                        onChange={handleChange}
                        className="md:col-span-1 w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-xs text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                        placeholder="Username / Email"
                    />
                    <input
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        className="md:col-span-1 w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-xs text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                        placeholder="Password"
                        type="text"
                    />
                    <input
                        name="url"
                        value={form.url}
                        onChange={handleChange}
                        className="md:col-span-1 w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-xs text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                        placeholder="Login URL"
                    />
                    <div className="md:col-span-1 flex items-center">
                        <button
                            type="button"
                            onClick={handleAdd}
                            className="w-full px-3 py-2 rounded text-xs font-bold bg-blue-600 text-white hover:bg-blue-700 uppercase border border-blue-500"
                        >
                            + Add
                        </button>
                    </div>
                </div>
                <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    className="w-full border border-gray-600 bg-gray-700 rounded px-3 py-2 text-xs text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500"
                    placeholder="Internal notes about this account (optional)"
                    rows={2}
                />

                <div className="mt-4 space-y-2">
                    {credentials && credentials.length > 0 ? (
                        credentials.map(cred => (
                            <div key={cred.id} className="p-3 bg-gray-900/40 rounded border border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <div className="space-y-1">
                                    <div className="text-sm text-white font-mono">
                                        {cred.label || 'Account'}{cred.username ? ` • ${cred.username}` : ''}
                                    </div>
                                    <div className="text-xs text-gray-400">
                                        {cred.url && (
                                            <a href={cred.url} target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 mr-2">
                                                Open
                                            </a>
                                        )}
                                        {cred.password_encrypted && (
                                            <CredentialPasswordDisplay password={cred.password_encrypted} />
                                        )}
                                    </div>
                                </div>
                                {cred.notes && (
                                    <div className="text-xs text-gray-400 max-w-xl">
                                        {cred.notes}
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="text-sm text-gray-400 text-center py-6">
                            No credentials recorded for this client yet.
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CaseCredentials;

const CredentialPasswordDisplay = ({ password }) => {
    const [show, setShow] = useState(false);
    return (
        <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded bg-gray-800 text-gray-300 border border-gray-700 text-[11px]">
            <span>{show ? password : '••••••••'}</span>
            <button
                onClick={() => setShow(!show)}
                className="text-gray-400 hover:text-white focus:outline-none"
                title={show ? "Hide password" : "Show password"}
            >
                {show ? (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                ) : (
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                )}
            </button>
        </span>
    );
};
